<template>
    <div v-click-outside="hide" class="simple-color-input">
        <default-title :class="`mr-${mr}`" small coal v-if="label"> {{ label }}: </default-title>
        <button
            v-if="!flat"
            ref="button"
            @mousedown.stop.prevent="toggle"
            :style="{ '--bg': modelValue }"
            class="input-circle cursor-pointer button-scale"
            :class="{ white: isWhite(modelValue) }"
        ></button>
        <transition name="fade-up">
            <div :style="modalStyle" v-if="isOpen || flat" class="simple-color-modal">
                <div class="input-circle-grid m-10">
                    <button
                        :title="color"
                        :key="index"
                        v-for="(color, index) in palette"
                        @mousedown.stop.prevent="setColor(color)"
                        :style="{ '--bg': color, outlineColor: color }"
                        :class="{ white: isWhite(color), active: isActive(color) }"
                        class="input-circle button-scale"
                    ></button>
                </div>

                <template v-if="usedColors && usedColors.length">
                    <span class="simple-color-label mx-10 mt-15 block"> {{ t("expert.own_colors") }}: </span>
                    <div class="input-circle-grid mx-10 mb-15 mt-8">
                        <div :key="index" v-for="(color, index) in usedColors" class="relative input-circle-wrapper">
                            <button
                                @mousedown.stop.prevent="removeUsedColor(index)"
                                class="w-12 remove-color h-12 flex items-center justify-center absolute"
                            >
                                <close-icon bold />
                            </button>
                            <button
                                :title="color"
                                @mousedown.stop.prevent="setColor(color)"
                                :style="{ '--bg': color, outlineColor: color }"
                                :class="{ white: isWhite(color), active: isActive(color) }"
                                class="input-circle button-scale"
                            ></button>
                        </div>
                    </div>
                </template>

                <div v-if="!showCustomColor" class="simple-color-custom p-10">
                    <button @mousedown.stop.prevent="showCustomColorMode()" class="simple-color-add">
                        + <span> {{ t("expert.add_custom_color") }}</span>
                    </button>
                </div>

                <div v-else class="p-10 simple-color-custom">
                    <div>
                        <div class="flex items-center">
                            <span class="whitespace-nowrap">{{ t("expert.own_color") }}:</span>
                            <label
                                v-if="!isIOS()"
                                ref="label"
                                :class="{ white: isWhite(customColor), active: isActive(customColor) }"
                                :style="{ '--bg': customColor, outlineColor: customColor }"
                                class="ml-10 input-circle small button-scale type-color-wrapper cursor-pointer"
                            >
                                <input
                                    @input="handleInput"
                                    ref="input"
                                    :value="customColor"
                                    @change="customColor = $event.target.value"
                                    type="color"
                                />
                            </label>
                            <input
                                class="ml-10"
                                v-else
                                @input="handleInput"
                                :value="customColor"
                                ref="input"
                                @change="customColor = $event.target.value"
                                type="color"
                            />

                            <input :value="customColor" @input="handleTextInput" class="text-input" type="text" />
                        </div>
                        <div class="flex items-center mt-10">
                            <span class="whitespace-nowrap">{{ t("expert.alfa_channel") }}:</span>

                            <div class="range-input-wrapper">
                                <input
                                    :style="{
                                        border: `1px solid #eee`,
                                        background: `linear-gradient(45deg, transparent 20%, ${hexWithoutAlpha} )`
                                    }"
                                    class="range-input"
                                    v-model.number="currentAlpha"
                                    @input="handleAlphaChange"
                                    type="range"
                                    min="0"
                                    max="255"
                                    step="1"
                                />
                            </div>
                        </div>
                    </div>

                    <button @mousedown.stop.prevent="addCustomColor" class="add-button button-scale">
                        <span> {{ t("expert.save") }}</span>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import IsOpen from "../../../mixins/is-open"
import checkDevice from "../../../mixins/checkDevice"
import PopularColors from "../../Builder/components/ColorInput/popular-colors"
import SoftColors from "../../Builder/components/ColorInput/soft-colors"
import { EventBus } from "../../../utils/Events"
import { getPersonalSettings, updatePersonalSettings } from "../../../store/personal-settings"
import CloseIcon from "../../../icons/CloseIcon.vue"
import DefaultTitle from "../../Typography/DefaultTitle.vue"

export default {
    components: { DefaultTitle, CloseIcon },
    props: {
        fixed: {
            type: Boolean,
            default: false
        },
        modelValue: {
            type: String,
            default: ""
        },
        flat: {
            type: Boolean,
            default: false
        },
        showCustomColorOnSelect: {
            type: Boolean,
            default: true
        },
        mr: {
            type: String,
            default: "14"
        },
        label: {
            type: String,
            default: ""
        },
        usedKey: {
            type: String,
            default: "used_colors"
        },
        emitOnChange: {
            type: Boolean,
            default: true
        },
        softPalette: {
            type: Boolean,
            default: false
        }
    },
    model: {
        prop: "modelValue",
        event: "update:modelValue"
    },
    name: "BuilderSimpleColor",
    mixins: [IsOpen, checkDevice],
    data() {
        return {
            showCustomColor: false,
            customColor: "#000000",
            usedColors: [],
            currentAlpha: 255
        }
    },
    created() {
        this.usedColors = this.getUsedColors()

        EventBus.$on(`update-settings:${this.usedKey}`, () => {
            this.usedColors = this.getUsedColors()
        })

        EventBus.$on("paste:color", color => {
            if (this.isOpen && this.showCustomColor) {
                this.customColor = color
                this.$forceUpdate()
            }
        })
    },
    methods: {
        handleAlphaChange() {
            if (this.emitOnChange) {
                this.$emit("update:modelValue", this.computedColor)
            }
            this.customColor = this.computedColor
        },
        handleTextInput(event) {
            this.setCustomAlpha(event.target.value)
            this.handleInput(event)
        },
        handleInput(event) {
            if (this.emitOnChange) {
                const newHex = event.target.value
                this.$emit(
                    "update:modelValue",
                    this.currentAlpha === 255 ? newHex : `${newHex}${this.currentAlpha.toString(16).padStart(2, "0")}`
                )
            }

            this.customColor = event.target.value
        },
        getUsedColors() {
            return getPersonalSettings(this.usedKey) ?? []
        },
        addCustomColor() {
            this.setColor(this.customColor)

            if (window.user) {
                const colors = [this.customColor, ...this.getUsedColors()]
                updatePersonalSettings([{ key: this.usedKey, value: colors }])
            }

            this.showCustomColor = false
            this.customColor = "#000000"
        },
        isWhite(color) {
            return [
                "white",
                "#ffffff",
                "#fff",
                "#FFF",
                "#FFFFFF",
                "#00000000",
                "transparent",
                "rgba(255,255,255,0)"
            ].includes(color)
        },
        isActive(color) {
            return color === this.modelValue
        },
        onHide() {
            this.showCustomColor = false
            this.customColor = "#000000"
        },
        showCustomColorMode() {
            if (this.showCustomColor) {
                return
            }
            this.showCustomColor = true
            this.$nextTick(() => {
                // this.$refs.label.click()
            })
        },
        removeUsedColor(index) {
            const colors = this.getUsedColors()

            colors.splice(index, 1)

            updatePersonalSettings([{ key: this.usedKey, value: colors }])
        },
        setColor(color) {
            this.customColor = color

            this.$emit("set:color")
            this.$emit("update:modelValue", color)

            if (this.showCustomColorOnSelect) {
                this.showCustomColorMode()
            }
        },
        setCustomAlpha(val) {
            this.currentAlpha = val.length === 9 ? parseInt(val.slice(7, 9), 16) : 255
        }
    },
    computed: {
        palette() {
            return this.softPalette ? SoftColors : PopularColors
        },
        initialAlpha() {
            if (this.customColor.length === 9) {
                // #RRGGBBAA
                return parseInt(this.customColor.slice(7, 9), 16)
            }
            return 255
        },
        hexWithoutAlpha() {
            return this.customColor.slice(0, 7)
        },
        computedColor() {
            return this.currentAlpha === 255
                ? this.hexWithoutAlpha
                : `${this.hexWithoutAlpha}${this.currentAlpha.toString(16).padStart(2, "0")}`
        },
        modalStyle() {
            const style = {}

            if (this.fixed && this.$refs.button) {
                const { top, left } = this.$refs.button.getBoundingClientRect()
                style.position = `fixed`
                //style.transform = "translateY(-100%)"
                style.top = `${top}px !important`
                style.left = `${left + 30}px !important`
                style.transform = `translateX(-100%) !important`
                style.bottom = "auto"
                style.zIndex = "99999"
            }
            return style
        }
    },
    watch: {
        customColor: {
            immediate: true,
            handler() {
                this.currentAlpha = this.initialAlpha
            }
        }
    }
}
</script>
<style lang="scss" scoped>
.remove-color {
    background-color: #fff;
    right: -2px;
    top: -2px;
    z-index: 2;
    border-radius: 2px;
    box-shadow: 0 2px 5px rgba(128, 158, 191, 0.25);
    display: none;
    svg {
        height: 8px;
        width: 8px;
    }
}

.input-circle-wrapper {
    &:hover {
        .remove-color {
            display: flex;
        }
    }
}

.type-color-wrapper {
    input[type="color"] {
        visibility: hidden;
    }
}

.add-button {
    width: 100%;
    color: #fff;
    margin-top: 15px;
    font-size: 14px;
    height: 28px;
    border-radius: 4px;
    background-color: #396fff;
    transition: 0.1s;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: #3566ec;
    }
}

.simple-color-custom {
    background-color: #fbfbfb;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    border-top: 1px solid #eee;
    display: flex;
    flex-direction: column;
}

.simple-color-add {
    font-size: 14px;
    font-weight: 500;
    color: #396fff;

    span {
        text-underline-offset: 5px;
    }

    &:hover {
        span {
            text-decoration: underline;
        }
    }
}

.text-input {
    margin-left: 10px;
    width: 100%;
    border: 1px solid #c1c1c1;
    height: 22px;
    text-indent: 4px;
    border-radius: 4px;
}

.range-input-wrapper {
    width: 100%;
    height: 6px;
    margin-left: 10px;
    display: flex;
    align-items: center;
    border-radius: 5px;
    background-size: 8px 8px;
    background-position: 0 0, 4px 4px;
    background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%),
        linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%);
}

.range-input {
    margin: 0;
    appearance: none;
    cursor: pointer;
    width: 100%;
    height: 6px;
    border-radius: 5px;
    outline: none;
    position: relative;
    z-index: 1;

    &::before {
        content: "";
        background-color: #fbfbfb;
    }

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        border-radius: 50%;
        width: 12px;
        height: 12px;
        background: #ffffff;
        border: 1px solid #98a2b0;
        cursor: pointer;
    }

    &::-moz-range-thumb {
        border-radius: 50%;
        width: 12px;
        height: 12px;
        background: #ffffff;
        border: 1px solid #98a2b0;
        cursor: pointer;
    }
}

.input-circle-grid {
    display: grid;
    grid-gap: 7px;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
}

.simple-color-input {
    height: 30px;
    width: max-content;
    position: relative;
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: #323030;
}

.simple-color-modal {
    border-radius: 10px;
    position: absolute;
    box-shadow: 0 4px 10px rgba(106, 110, 119, 0.35);
    z-index: 9999;
    top: 0;
    background-color: #fff;
    max-width: 220px;
}

.input-circle {
    width: 28px;
    border-radius: 50%;
    height: 28px;
    border: 1px solid transparent;
    cursor: pointer;
    outline-offset: 2px;
    outline: transparent none 2px;
    transition: outline 0.1s !important;
    position: relative;

    &.small {
        width: 20px;
        height: 20px;
    }

    &::before,
    &::after {
        width: 100%;
        height: 100%;
        inset: 0;
        z-index: 0;
        border-radius: 50%;
        content: "";
        position: absolute;
    }

    &::after {
        background-color: var(--bg);
        z-index: 1;
    }

    &::before {
        background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%),
            linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%);
        background-size: 8px 8px;
        background-position: 0 0, 4px 4px;
    }

    &.active {
        outline-style: dashed;
    }

    &.white {
        border: 1px solid #d8d8d8;
        outline-color: #d3d3d3 !important;
    }

    &.cursor-pointer {
        cursor: pointer;
    }

    &:active {
        &::before {
            content: none;
        }
    }
}
</style>
